// Trips-homepage model (design_handoff_homepage). Classify real trips by date
// into upcoming / active / past (+ a drafts lane for trips without locked
// dates). Per-trip color/photo key off a representative city. now = real today.

import { paletteForDestIds } from './city-colors';
import { daysBetween, localDateISO } from './dates';
import type { TripListItem } from './types';

export type TripState = 'upcoming' | 'active' | 'past' | 'draft';
export type StatusTone = 'live' | 'soon' | 'up' | 'past';

export interface HomeTrip {
  id: string;
  name: string;
  start?: string;
  end?: string;
  nights: number;
  stops: number;
  eyebrow: string;
  city: string; // representative city → color + hero photo
  draft: boolean;
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// noon-anchored to dodge DST when differencing whole days
function d(s: string): Date {
  return new Date(`${s}T12:00:00Z`);
}
function days(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

export function deriveHomeTrips(trips: TripListItem[]): HomeTrip[] {
  return trips.map((t) => {
    const start = t.dates?.lockedStart;
    const end = t.dates?.lockedEnd;
    const stays = t.stays ?? [];
    // skip raw external ids (place:..., long opaque slugs) so they don't surface
    // as an eyebrow / photo query.
    const clean = (c: string) => !/^place:/i.test(c) && !/^[a-f0-9-]{16,}$/i.test(c);
    const cities = [
      ...new Set(stays.map((s) => s.label || s.destId).filter((c): c is string => !!c && clean(c))),
    ];
    const city = cities[0] || t.shortName || t.name;
    const eyebrow = cities.slice(0, 2).join(' · ') || t.shortName || '';
    const nights =
      t.dates?.lockedNights ?? (start && end ? Math.max(0, days(d(start), d(end))) : 0);
    return {
      id: t.tripId,
      name: t.name,
      start,
      end,
      nights,
      stops: stays.length,
      eyebrow,
      city,
      draft: !!t.draft,
    };
  });
}

export function tripState(t: HomeTrip, now: Date): TripState {
  // Honor the real meta.draft flag first — mobile filters drafts out of every
  // main list on `t.draft === true` (mobile/lib/trip-registry.ts getActiveTrips),
  // regardless of whether the draft has locked dates. Web previously inferred
  // "draft" only from missing dates, so a dated test draft ("June 2026") leaked
  // into Upcoming. Drafts now route to the Drafts lane like on mobile.
  if (t.draft) return 'draft';
  if (!t.start || !t.end) return 'draft';
  // Compare calendar dates on the viewer's clock — a noon-UTC instant
  // flipped Hawaiʻi trips to 'past' at 2 AM on departure day.
  const today = localDateISO(now);
  if (today < t.start) return 'upcoming';
  if (today > t.end) return 'past';
  return 'active';
}

export interface Status {
  label: string;
  tone: StatusTone;
}
export function tripStatus(t: HomeTrip, now: Date): Status {
  const st = tripState(t, now);
  if (st === 'draft') return { label: 'Draft', tone: 'up' };
  const today = localDateISO(now);
  if (st === 'active') {
    const day = daysBetween(t.start!, today) + 1;
    return { label: `Day ${day} of ${t.nights + 1}`, tone: 'live' };
  }
  if (st === 'upcoming') {
    const n = daysBetween(today, t.start!);
    if (n <= 1) return { label: 'Tomorrow', tone: 'soon' };
    if (n < 21) return { label: `in ${n} days`, tone: 'soon' };
    if (n < 60) return { label: `in ${Math.round(n / 7)} weeks`, tone: 'up' };
    const m = Math.round(n / 30);
    return { label: `in ${m} month${m > 1 ? 's' : ''}`, tone: 'up' };
  }
  const n = daysBetween(t.end!, today);
  if (n < 30) return { label: `${n}d ago`, tone: 'past' };
  if (n < 365) return { label: `${Math.round(n / 30)}mo ago`, tone: 'past' };
  return { label: `${Math.floor(n / 365)}y ago`, tone: 'past' };
}

export function fmtRange(t: HomeTrip): string {
  if (!t.start || !t.end) return 'Dates TBD';
  const a = d(t.start);
  const b = d(t.end);
  const sameYear = a.getUTCFullYear() === b.getUTCFullYear();
  const left = `${MON[a.getUTCMonth()]} ${a.getUTCDate()}`;
  const right =
    a.getUTCMonth() === b.getUTCMonth() && sameYear ? `${b.getUTCDate()}` : `${MON[b.getUTCMonth()]} ${b.getUTCDate()}`;
  return `${left} – ${right}, ${b.getUTCFullYear()}`;
}

export function startTime(t: HomeTrip): number {
  return t.start ? d(t.start).getTime() : 0;
}

// de-collided Editorial palette across every trip's representative city.
export function homePalette(trips: HomeTrip[]): Record<string, { c1: string; c2: string }> {
  return paletteForDestIds(trips.map((t) => t.city));
}

export function earliestYear(trips: HomeTrip[]): number | null {
  const years = trips.map((t) => (t.start ? d(t.start).getUTCFullYear() : null)).filter((y): y is number => y != null);
  return years.length ? Math.min(...years) : null;
}
